/**
 * Guided-tour script. Steps are anchored to real UI by `data-tour` attributes rather than by
 * class names, so restyling a component can't silently break the tour. A step whose anchor never
 * appears (role-gated nav item, element removed) is skipped at runtime — see TourOverlay.
 */

export type TourMenu = 'resources' | 'user';

export interface TourStep {
  id: string;
  title: string;
  body: string;
  /** Selector(s) for the element(s) to spotlight; the union of all matches is lit. Omit for a centered card. */
  anchor?: string;
  /** Route the tour must be on before this step shows. */
  route?: string;
  /** Nav dropdown held open while this step is showing. */
  menu?: TourMenu;
  /** Capability the caller needs; steps they can't use are dropped when the tour starts. */
  capability?: string;
}

export const TOUR_STEPS: readonly TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to EFTCreator',
    body:
      "Here's the 60-second version: install the Device Service, check your scanner, capture a " +
      'transaction, and track it through to the results. Use Next to step through, or skip any time.',
  },
  {
    id: 'downloads',
    title: 'Start by installing the Device Service',
    body:
      'Scanners are driven by a small service that runs on the workstation, not in the browser. ' +
      'Download and install it from here on each machine you capture from.',
    anchor: '[data-tour="nav-downloads"]',
    menu: 'resources',
  },
  {
    id: 'device-status',
    title: 'Confirm your equipment',
    body:
      'Device status tells you whether the service is running, which scanners it can see, and which ' +
      'device libraries are loaded. Check here first whenever a capture will not start.',
    anchor: '[data-tour="nav-device-status"]',
    menu: 'resources',
  },
  {
    id: 'capture',
    title: 'Capture a transaction',
    body:
      'New EFT walks you through one workflow end to end: applicant details, fingerprints, and any ' +
      'photos or signatures the form requires. Fields are validated as you go, so nothing is ' +
      'submitted incomplete.',
    anchor: '[data-tour="nav-capture"]',
    capability: 'Capture',
  },
  {
    id: 'quick-actions',
    title: 'Or jump straight to a workflow',
    body:
      'The dashboard shortcuts skip the picker and open a specific workflow — ATF eForm 1 & 4, DOCE ' +
      'if your account is provisioned for it, or a card conversion. If you are out of conversion ' +
      'credits, you can buy one right at submit time without leaving the flow.',
    anchor: '[data-tour="quick-actions"]',
    route: '/app',
  },
  {
    id: 'transactions',
    title: 'Track everything you have submitted',
    body:
      'Every capture lands here with its current state. From a transaction you can review what was ' +
      'captured, correct and resubmit a rejected EFT, and download the EFT file itself.',
    anchor: '[data-tour="nav-transactions"]',
  },
  {
    id: 'store',
    title: 'Scanners and supplies',
    body:
      'The store is the hardware side: scanners, fingerprint cards, and accessories. Plans and ' +
      'conversion credits are billing, not store items — those live under Subscriptions & billing.',
    anchor: '[data-tour="nav-store"]',
  },
  {
    id: 'account',
    title: 'Your account lives here',
    body:
      'Your profile and past orders, plus — depending on your role — Subscriptions & billing, where ' +
      'you choose a capture plan and buy card-conversion credits, along with device licenses and ' +
      'your team.',
    anchor: '[data-tour="nav-user"], [data-tour="user-menu"]',
    menu: 'user',
  },
  {
    id: 'assistant',
    title: 'Ask the EFTCreator assistant',
    body:
      'The assistant is on every page. Ask it a question in plain language — it can look up your own ' +
      'account details to answer, and if it cannot resolve something it will open a support ticket ' +
      'for you and hand it to our team.',
    anchor: '[data-tour="support-fab"]',
  },
  {
    id: 'finish',
    title: "That's the whole system",
    body:
      'If something is not behaving, start with Device status for anything hardware-related. You can ' +
      'replay this tour any time from Resources → Getting started.',
  },
];
